/**
 * Teacher Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { TeacherUpdate } from '../../types/teacher';

const TeacherEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<TeacherUpdate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department: '',
    hire_date: '',
    is_school_admin: false,
    is_consultant: false,
    github_username: '',
    github_token: '',
    github_repositories: null,
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!id) {
        setError('No teacher ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/teachers/${id}/`);
        const teacher = response.data;

        setFormData({
          first_name: teacher.first_name || '',
          last_name: teacher.last_name || '',
          email: teacher.email || '',
          phone: teacher.phone || '',
          department: teacher.department || '',
          hire_date: teacher.hire_date || '',
          is_school_admin: teacher.is_school_admin ?? false,
          is_consultant: teacher.is_consultant ?? false,
          github_username: teacher.github_username || '',
          github_token: teacher.github_token || '',
          github_repositories: teacher.github_repositories || null,
          is_active: teacher.is_active ?? true,
        });
      } catch (err: any) {
        console.error('Error fetching teacher:', err);
        if (err.response?.status === 404) {
          setError('Teacher not found');
        } else {
          setError('Failed to load teacher data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchTeacher();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    // All fields are optional for updates
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: TeacherUpdate = {};

      if (formData.first_name && formData.first_name.trim() !== '') payload.first_name = formData.first_name;
      if (formData.last_name && formData.last_name.trim() !== '') payload.last_name = formData.last_name;
      if (formData.email && formData.email.trim() !== '') payload.email = formData.email;
      if (formData.phone && formData.phone.trim() !== '') payload.phone = formData.phone;
      if (formData.department && formData.department.trim() !== '') payload.department = formData.department;
      if (formData.hire_date && formData.hire_date.trim() !== '') payload.hire_date = formData.hire_date;
      if (formData.github_username && formData.github_username.trim() !== '') payload.github_username = formData.github_username;
      if (formData.github_token && formData.github_token.trim() !== '') payload.github_token = formData.github_token;
      if (formData.github_repositories) payload.github_repositories = formData.github_repositories;
      payload.is_school_admin = formData.is_school_admin;
      payload.is_consultant = formData.is_consultant;
      payload.is_active = formData.is_active;

      await axios.patch(`/api/teachers/${id}/`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/teachers');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating teacher:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update teacher. Please check your input.');
        }
      } else {
        setError('Failed to update teacher. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.first_name) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/teachers')} sx={{ mt: 2 }}>
          Back to Teachers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/teachers')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Teachers
        </MuiLink>
        <Typography color="text.primary">Edit Teacher</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Teacher</Typography>
        <Typography variant="body2" color="textSecondary">Update the teacher information</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Teacher updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="First Name" name="first_name"
                value={formData.first_name || ''} onChange={handleChange}
                error={!!validationErrors.first_name} helperText={validationErrors.first_name}
                disabled={loading}
              />
              <TextField
                fullWidth label="Last Name" name="last_name"
                value={formData.last_name || ''} onChange={handleChange}
                error={!!validationErrors.last_name} helperText={validationErrors.last_name}
                disabled={loading}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={formData.email || ''} onChange={handleChange}
                error={!!validationErrors.email} helperText={validationErrors.email}
                disabled={loading}
              />
              <TextField
                fullWidth label="Phone" name="phone"
                value={formData.phone || ''} onChange={handleChange}
                disabled={loading}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Department" name="department"
                value={formData.department || ''} onChange={handleChange}
                error={!!validationErrors.department} helperText={validationErrors.department}
                disabled={loading}
              />
              <TextField
                fullWidth type="date" label="Hire Date" name="hire_date"
                value={formData.hire_date || ''} onChange={handleChange}
                error={!!validationErrors.hire_date} helperText={validationErrors.hire_date}
                disabled={loading} InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="GitHub Username" name="github_username"
                value={formData.github_username || ''} onChange={handleChange}
                disabled={loading}
              />
              <TextField
                fullWidth label="GitHub Token" name="github_token" type="password"
                value={formData.github_token || ''} onChange={handleChange}
                disabled={loading}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_school_admin ?? false}
                    onChange={handleChange}
                    name="is_school_admin"
                    disabled={loading}
                  />
                }
                label="School Admin"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_consultant ?? false}
                    onChange={handleChange}
                    name="is_consultant"
                    disabled={loading}
                  />
                }
                label="Consultant"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active ?? true}
                    onChange={handleChange}
                    name="is_active"
                    disabled={loading}
                  />
                }
                label="Active"
              />
            </Stack>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/teachers')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Teacher'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default TeacherEditForm;
